import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {ErrorFlatButton} from 'src/components/error-flat-button/error-flat-button';
import {RequiredMark} from 'src/components/required-mark';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {AccountTeamMemberSuggest} from 'src/modules/create-account/components/account-team-member-suggest';
import {NO_PRIMARY_ATM, NO_PRIMARY_ATM_MESSAGE} from 'src/modules/create-account/constants';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {saveAccountManagerRelations} from 'src/openapi/admin/apis/AccountManagerRelationsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button, {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Info} from '@pb/kit-component-info';
import {RadioButton} from '@pb/kit-component-radio-button';


import {ACCOUNT_TEAM_FULL_MESSAGE} from '../constant';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccountTeamMember} from 'src/modules/create-account/typedef';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';

type Props = {
    accountId: number,
    onClose: () => void,
    onSuccessClose: () => void,
};

export const ACCOUNT_TEAM_MEMBER_MODAL_WIDTH = 1215;

export const AccountTeamMemberEditModal: React.FC<Props> = ({onClose, accountId, onSuccessClose}) => {
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);
    const {accountManagerTypes, filteredAccountManagerTypes} = useAccountManagerTypes();
    const {accountManagers} = useSelector(selectAccountDetails);
    const {accountFieldChange} = useEventTracking();

    const onFieldChange = useCallback((parameterName: string) => {
        accountFieldChange({
            parameterName,
            viewType: 'Existed',
            section: 'Edit Account Team',
        });
    }, [accountFieldChange]);

    const [initialAccountTeamMembers, setInitialAccountTeamMembers] = useState<Array<AccountTeamMember>>([]);
    const [accountTeamMembers, setAccountTeamMembers] = useState<Array<AccountTeamMember>>([]);

    const [busy, setBusy] = useState(false);
    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);

    useEffect(() => {
        if (accountManagers) {
            const teamMembers = accountManagers.length ? accountManagers.map(manager => ({
                user: {
                    id: manager.id,
                    fullName: manager.fullName,
                    email: manager.email,
                    avatar: manager.avatar,
                },
                primary: manager.primary,
                type: manager.managerType,
                receiveAccountAlerts: manager.receiveAccountAlerts,
            })) : [{primary: true, type: '', receiveAccountAlerts: true}];

            setAccountTeamMembers(teamMembers);
            setInitialAccountTeamMembers(teamMembers);
        }
    }, [accountManagers]);

    useEffect(() => {
        accountTeamMembers.forEach(({primary, user}, id) => {
            if (primary && !user) {
                addError({
                    code: NO_PRIMARY_ATM,
                    message: NO_PRIMARY_ATM_MESSAGE,
                    controlId: `account-team-member-select-${id}`,
                });
            } else {
                removeError(`account-team-member-select-${id}`);
            }
        });
    }, [accountTeamMembers, addError, removeError]);

    const onSetAccountManager = (id: number, selectedUser?: AccountManagerSearchResult) => {
        setAccountTeamMembers(teamMembers => teamMembers.map((teamMember, i) => ({
            ...teamMember,
            user: i === id ? selectedUser : teamMember.user,
        })));
    };

    const onSetRole = (id: number, role: string) => {
        setAccountTeamMembers(teamMembers => teamMembers.map((teamMember, i) => ({
            ...teamMember,
            type: i === id ? role : teamMember.type,
        })));
    };

    const onSetPrimary = (id: number) => {
        onFieldChange('Primary');
        setAccountTeamMembers(teamMembers => teamMembers.map((teamMember, i) => ({
            ...teamMember,
            primary: i === id,
            receiveAccountAlerts: i === id || teamMember.receiveAccountAlerts,
        })));
    };

    const onSetReceiveAlerts = (id: number, data: {event: React.SyntheticEvent<HTMLInputElement, Event>}) => {
        onFieldChange('Receive Alerts');
        setAccountTeamMembers(teamMembers => teamMembers.map((teamMember, i) => ({
            ...teamMember,
            receiveAccountAlerts: i === id ? data.event.currentTarget.checked : teamMember.receiveAccountAlerts,
        })));
    };

    const getAllowedTypes = useCallback((selectedType?: string) => {
        const types = accountTeamMembers.map(({type}) => type);
        return filteredAccountManagerTypes.filter(type => !types.includes(type.code) || (type.code === selectedType));
    }, [accountTeamMembers, filteredAccountManagerTypes]);

    const getAllTypes = useCallback((selectedType?: string) => {
        const types = accountTeamMembers.map(({type}) => type);
        return accountManagerTypes.filter(type => !types.includes(type.code) || (type.code === selectedType));
    }, [accountTeamMembers, accountManagerTypes]);

    const onAddAccountTeamMember = () => {
        setAccountTeamMembers(teamMembers => [
            ...teamMembers,
            {
                primary: false,
                type: getAllowedTypes()[0].code,
                receiveAccountAlerts: true,
            },
        ]);
    };

    const onRemoveAccountTeamMember = (removeId: number) => {
        setAccountTeamMembers(teamMembers => teamMembers.filter((member, i) => i !== removeId));
    };

    const onSave = useCallback(async () => {
        setBusy(true);
        const updateATMRelationsResponse = await apiDispatch(saveAccountManagerRelations({
            accountId,
            saveManagerRelationsRequest: {
                relations: accountTeamMembers.filter(({user}) => user).map(teamMember => ({
                    accountManagerId: teamMember.user?.id,
                    primary: teamMember.primary,
                    receiveAlert: teamMember.receiveAccountAlerts,
                    typeCode: teamMember.type,
                })),
            },
        }));

        if (updateATMRelationsResponse.payload.success) {
            onSuccessClose();
        } else {
            setErrorMessage(getError({
                error: updateATMRelationsResponse.payload.data,
                separator: '. ',
            }));
        }
        setBusy(false);
    }, [accountTeamMembers, accountId, apiDispatch, onSuccessClose, setErrorMessage]);

    const saveDisabled = useMemo(() => accountTeamMembers.some(({user}) => !user) || isDeepEqual({
        prev: accountTeamMembers,
        current: initialAccountTeamMembers,
    }), [accountTeamMembers, initialAccountTeamMembers]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Edit Account Team
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="s" alignItems="start">
                    <Group gap="xl">
                        <WidthItem width={430}>
                            <RequiredMark label={<strong>Member</strong>}/>
                        </WidthItem>
                        <WidthItem width={315}>
                            <strong>Role</strong>
                        </WidthItem>
                        <strong>Options</strong>
                    </Group>
                    {accountTeamMembers.map((manager, id) => (
                        <Group gap="xl" alignItems="start" key={id}>
                            <ValidatedControl name={`account-team-member-select-${id}`}>
                                {invalid => (
                                    <AccountTeamMemberSuggest
                                        id={`account-team-member-select-${id}`}
                                        placeholder="Account team member"
                                        selectedTeamMember={manager.user}
                                        selectedTeamMembers={accountTeamMembers.map(({user}) => user)}
                                        onSelect={user => {
                                            onFieldChange('Account Team Member');
                                            onSetAccountManager(id, user);
                                        }}
                                        color={invalid ? 'ERROR' : 'DEFAULT'}
                                    />
                                )}
                            </ValidatedControl>
                            <WidthItem width={315}>
                                <ComboBox
                                    value={manager.type}
                                    onChange={role => {
                                        onFieldChange('Role');
                                        onSetRole(id, role);
                                    }}>
                                    {getAllTypes(manager.type)
                                        .map(({code, description}) => (
                                            !code
                                                ? <DefaultItem key="default" caption="None (deprecated)" value="" disabled={true}/>
                                                : <ComboboxItem key={code} caption={description} value={code}/>
                                        ))}
                                </ComboBox>
                            </WidthItem>
                            <Item className="group-item pdy-xs">
                                <Group gap="xl">
                                    <RadioButton
                                        value={id}
                                        label="Primary"
                                        checked={manager.primary}
                                        onChange={onSetPrimary}
                                    />
                                    <CheckBox
                                        disabled={manager.primary}
                                        checked={manager.receiveAccountAlerts}
                                        label="Account Alerts"
                                        onCheckBoxChange={data => onSetReceiveAlerts(id, data)}
                                    />
                                    {!manager.primary ? (
                                        <ErrorFlatButton
                                            icon={Icons.TIMES_CIRCLE}
                                            label="Remove"
                                            onClick={() => {
                                                onFieldChange('Remove Account Team Member');
                                                onRemoveAccountTeamMember(id);
                                            }}
                                        />
                                    ) : null}
                                </Group>
                            </Item>
                        </Group>
                    ))}
                    <Group direction="h" alignItems="center">
                        <FlatButton
                            disabled={accountTeamMembers.some(({user}) => !user) || getAllowedTypes().length === 0}
                            startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                            onClick={onAddAccountTeamMember}>
                            Add Account Team Member
                        </FlatButton>
                        {getAllowedTypes().length === 0 ? <Info type={{popover: ACCOUNT_TEAM_FULL_MESSAGE}}/> : null}
                    </Group>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button busy={busy} variant="Type.PRIMARY" disabled={saveDisabled} onClick={onSave}>
                        Save
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
